import { z } from 'zod';
import type { Font, Styles } from '../graphql/query/getRetailerSettings';

const fontFaceSchema = z.object({
  fontFamily: z
    .string()
    .max(100)
    .regex(/^[\w\s-]+$/),
  fontWeight: z.enum(['300', 'normal', 'bold']),
  src: z.string(),
});

export const themeSchema = z.object({
  ctaColor: z.string().optional(),
  primaryColor: z.string(),
  secondaryColor: z.string().optional(),
  textColor: z.string().optional(),
  fontFamily: z.string().optional(),
});

export type ThemeSchemaType = z.infer<typeof themeSchema>;

export const validateThemeData = (themeData: ThemeSchemaType) => {
  const result = themeSchema.safeParse(themeData);
  return result;
};

/**
 * Maps theme schema keys to their corresponding CSS custom properties.
 * Note: ctaColor is handled separately via generateCtaColorCss() to derive hover/pressed states.
 */
export const pdxThemeMapping: Partial<
  Record<keyof ThemeSchemaType, readonly string[]>
> = {
  primaryColor: [
    '--n-color-component-action-plain-content-default',
    '--n-color-border-focus-ring',
  ],
  secondaryColor: ['--n-color-component-action-secondary-fill-default'],
  textColor: ['--n-color-content-neutral-base'],
  fontFamily: ['--n-font-family-base'],
} as const;

/**
 * Generates CSS custom properties for CTA color with derived hover/pressed states
 * using CSS color-mix() to darken the base color progressively.
 */
const generateCtaColorCss = (ctaColor: string): string => {
  return [
    `--n-color-component-action-primary-fill-default: ${ctaColor};`,
    `--n-color-component-action-primary-fill-hover: color-mix(in srgb, ${ctaColor}, black 12%);`,
    `--n-color-component-action-primary-fill-pressed: color-mix(in srgb, ${ctaColor}, black 40%);`,
  ].join('\n  ');
};

const generateFontFaceCss = (fontConfig: Font): string => {
  if (!fontConfig || fontConfig.length === 0) {
    return '';
  }

  return fontConfig
    .map((font) => {
      const parsed = fontFaceSchema.safeParse(font);
      if (!parsed.success) {
        // eslint-disable-next-line no-console
        console.warn('Rejected unsafe font‑face payload', parsed.error.issues);
        return null;
      }

      const { fontFamily, fontWeight, src } = parsed.data;

      const escapedFamily = /\s/.test(fontFamily)
        ? `'${CSS.escape(fontFamily)}'`
        : CSS.escape(fontFamily);

      return (
        `@font-face {\n` +
        `  font-family: ${escapedFamily};\n` +
        `  font-weight: ${fontWeight};\n` +
        `  src: ${src}\n` +
        `}`
      );
    })
    .filter((rule): rule is string => rule !== null)
    .join('\n\n');
};

export const injectRetailerTheme = ({
  styles,
  options,
}: {
  options?: {
    id?: string;
    layer?: string;
    retailer?: string | null;
  };
  styles: Styles;
}) => {
  const { theme } = styles;
  const { id = 'retailerTheme', layer = 'retailer', retailer } = options || {};

  const themeStyleTag = document.getElementById(id);

  if (
    themeStyleTag &&
    themeStyleTag.getAttribute('data-retailer') === retailer
  ) {
    return;
  }

  const { data: validThemeData, success: isThemeDataValid } = validateThemeData(
    theme as ThemeSchemaType,
  );

  const fontFaceCss = generateFontFaceCss(styles?.font);
  let primaryFontFamily: string | null = null;
  if (styles?.font && styles.font.length > 0 && styles?.font?.[0]?.fontFamily) {
    // TODO: consider supporting multiple font families
    const normalFont = styles.font.find(
      (e) => e.fontWeight === 'normal',
    )?.fontFamily;
    primaryFontFamily = normalFont ? normalFont : styles.font[0].fontFamily;
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      `Could not determine primary font family from styles.typography for retailer: ${
        retailer ?? 'unknown'
      }`,
    );
  }

  const hasValidThemeEntries =
    isThemeDataValid &&
    validThemeData &&
    Object.keys(validThemeData).length > 0;
  const hasFontFaces = fontFaceCss.length > 0;
  const hasPrimaryFontFamily = primaryFontFamily !== null;

  if (!hasValidThemeEntries && !hasFontFaces && !hasPrimaryFontFamily) {
    // eslint-disable-next-line no-console
    console.error(
      'No valid theme data or font faces to inject for retailer:',
      retailer ?? 'unknown',
    );
    return;
  }

  let cssCustomProperties = '';
  if (hasValidThemeEntries && validThemeData) {
    const standardProperties = Object.entries(validThemeData)
      .filter(([key]) => key !== 'fontFamily' && key !== 'ctaColor')
      .map((entry) => {
        if (entry[1] === null) return null;

        const [key, value] = entry as [keyof ThemeSchemaType, string];
        let pdxThemeMappedValue = pdxThemeMapping[key];

        // Temporary StitchFix override – remove when redundant
        if (
          pdxThemeMappedValue &&
          key === 'textColor' &&
          retailer === 'stitchfix' &&
          !pdxThemeMappedValue.includes(
            '--n-color-component-action-primary-content-default',
          )
        ) {
          pdxThemeMappedValue = [
            ...pdxThemeMappedValue,
            '--n-color-component-action-primary-content-default',
          ];
        }

        return pdxThemeMappedValue && pdxThemeMappedValue.length > 0
          ? pdxThemeMappedValue
              .map((cssCustomProp) => `${cssCustomProp}: ${value};`)
              .join('\n  ')
          : null;
      })
      .filter((prop): prop is string => prop !== null)
      .join('\n  ');

    // Generate CTA color with derived hover/pressed states using color-mix()
    const ctaColorCss = validThemeData.ctaColor
      ? generateCtaColorCss(validThemeData.ctaColor)
      : '';

    cssCustomProperties = [standardProperties, ctaColorCss]
      .filter(Boolean)
      .join('\n  ');
  }

  const fontFamilyCssCustomProperty =
    hasPrimaryFontFamily && pdxThemeMapping.fontFamily?.[0]
      ? `${pdxThemeMapping.fontFamily[0]}: ${primaryFontFamily};`
      : '';

  const style = document.createElement('style');
  style.id = id;
  style.setAttribute('data-layer', layer);
  if (retailer) style.setAttribute('data-retailer', retailer);

  let rootBlockContent = [cssCustomProperties, fontFamilyCssCustomProperty]
    .filter(Boolean)
    .join('\n  ');

  // Add themeVarOverrides as CSS custom properties in :root
  let themeVarOverridesBlock = '';
  if (
    Array.isArray(styles.themeVarOverrides) &&
    styles.themeVarOverrides.length > 0
  ) {
    themeVarOverridesBlock = styles.themeVarOverrides
      .map((pair) => {
        if (
          Array.isArray(pair) &&
          pair.length === 2 &&
          typeof pair[0] === 'string' &&
          typeof pair[1] === 'string'
        ) {
          const [cssVar, value] = pair;
          return `${cssVar}: ${value};`;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n  ');
  }

  // Combine all :root custom properties
  const allRootBlockContent = [rootBlockContent, themeVarOverridesBlock]
    .filter(Boolean)
    .join('\n  ');

  let cssContent = `@layer ${layer} {\n`;
  if (hasFontFaces) {
    cssContent += `\n${fontFaceCss}\n\n`;
  }
  if (allRootBlockContent) {
    cssContent += `  :root {\n  ${allRootBlockContent} \n  }\n`;
  }
  cssContent += `}`;

  style.textContent = cssContent;

  document.head.appendChild(style);
};
